import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Music, Trophy, Landmark, Cpu, Sparkles, Plus, BookOpen, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { pageSEO } from "@/components/SEO";
import UtilityHeader, { StickyUtilityBar } from "@/components/UtilityHeader";
import { usePageView } from "@/hooks/useAnalytics";
import { 
  HomeDataProvider, 
  ThisWeekendQuickOptimized, 
  FreeEventsQuickOptimized, 
  MoviesQuickOptimized,
  ConcertsQuickOptimized,
  BoringWeekendFixOptimized,
  useEventCount
} from "@/components/QuickListsOptimized";
import { WhatsAppShareCard, WhatsAppFloatingButton } from "@/components/WhatsAppShareBanner";
import { SpotlightSection } from "@/components/SpotlightSection";

// Lazy load non-critical components to improve LCP
const CountdownHero = lazy(() => import("@/components/CountdownHero"));
const ThisWeekSection = lazy(() => import("@/components/ThisWeekSection"));
const TrendingSection = lazy(() => import("@/components/TrendingSection"));
const HolidaySection = lazy(() => import("@/components/HolidaySection"));
const StateFilter = lazy(() => import("@/components/StateFilter"));
const SubmitEventModal = lazy(() => import("@/components/SubmitEventModal"));
const BookmarkTrigger = lazy(() => import("@/components/BookmarkTrigger"));
const NewsletterSection = lazy(() => import("@/components/NewsletterSection"));
const WhatsAppShareBanner = lazy(() => import("@/components/WhatsAppShareBanner"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section skeleton for lazy loaded content
function SectionSkeleton() {
  return <div className="h-48 bg-white/5 rounded-2xl animate-pulse" />;
}

const categoryConfig = {
  music: { icon: Music, color: "violet", gradient: "from-violet-500 to-purple-500" },
  sports: { icon: Trophy, color: "emerald", gradient: "from-emerald-500 to-teal-500" },
  cultural: { icon: Landmark, color: "amber", gradient: "from-amber-500 to-orange-500" },
  tech: { icon: Cpu, color: "sky", gradient: "from-sky-500 to-cyan-500" },
  lifestyle: { icon: Sparkles, color: "rose", gradient: "from-rose-500 to-pink-500" }
};

function HomeContent() {
  // Track page view
  usePageView("home");
  
  const [categories, setCategories] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const eventCount = useEventCount();

  const fetchNextEvent = async () => {
    try {
      const res = await axios.get(`${API}/events/next`);
      setNextEvent(res.data);
    } catch (error) {
      console.error("Error fetching next event:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, nextEventRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/events/next`)
        ]);
        setCategories(categoriesRes.data);
        setNextEvent(nextEventRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Refresh next event every 5 minutes (reduced from every API call)
    const refreshInterval = setInterval(fetchNextEvent, 300000);
    return () => clearInterval(refreshInterval);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO {...pageSEO.home} />
      <Header />
      <StickyUtilityBar />
      
      {/* ========== HERO - UTILITY FOCUSED ========== */}
      <section className="pt-32 pb-8 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          {/* Remove initial animation to reduce CLS and improve LCP */}
          <div>
            {/* Brand */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">UP</span>
              <span className="text-3xl md:text-4xl font-extrabold text-foreground">NEXT</span>
            </div>
            
            {/* Utility-first headline - NOT hype - This is LCP element */}
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground leading-tight mb-3">
              Tak payah fikir.{" "}
              <span className="bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
                Terus plan.
              </span>
            </h1>
            
            {/* Calm, confident copy */}
            <p className="text-base md:text-lg text-muted-foreground mb-6 max-w-xl mx-auto">
              Semua event Malaysia 2026. Satu tempat sahaja.
            </p>
            
            {/* Trust signals - fixed height to prevent CLS */}
            <div className="mb-8 min-h-[32px]">
              <UtilityHeader eventCount={eventCount} />
            </div>
            
            {/* Simple CTAs - fixed height to prevent CLS */}
            <div className="flex flex-wrap justify-center gap-3 min-h-[48px]">
              <Link 
                to="/events"
                className="px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
                data-testid="cta-events"
              >
                Tengok Minggu Ni
              </Link>
              <Link 
                to="/free-events"
                className="px-6 py-3 border border-white/20 text-foreground font-semibold rounded-xl hover:bg-white/5 transition-colors"
                data-testid="cta-free-events"
              >
                Event Percuma
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SPOTLIGHT - PROMOTED EVENT WITH TICKETS ========== */}
      <SpotlightSection />

      {/* ========== QUICK LISTS GRID - SHAREABLE SECTIONS ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ThisWeekendQuickOptimized />
            <FreeEventsQuickOptimized />
            <MoviesQuickOptimized />
          </div>
        </div>
      </section>

      {/* ========== NEXT 7 DAYS - HABIT LOOP ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-foreground">
                7 Hari Akan Datang
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Block dates ni sekarang
              </p>
            </div>
            <Link to="/events" className="text-pink-400 hover:text-pink-300 text-sm flex items-center gap-1 font-medium">
              Semua <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Suspense fallback={<SectionSkeleton />}>
            <ThisWeekSection onEventClick={(event) => navigate(`/event/${event.id}`)} />
          </Suspense>
        </div>
      </section>

      {/* ========== LOCATION RELEVANCE ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-foreground">
              Dekat Dengan Anda
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Pilih negeri untuk filter events
            </p>
          </div>
          <Suspense fallback={<SectionSkeleton />}>
            <StateFilter />
          </Suspense>
        </div>
      </section>

      {/* ========== BULAN NI - MONTHLY PLANNING ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-foreground">
                Bulan Ni
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Jangan miss dates penting
              </p>
            </div>
            <Link to="/calendar" className="text-violet-400 hover:text-violet-300 text-sm flex items-center gap-1 font-medium">
              Calendar penuh <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Suspense fallback={<SectionSkeleton />}>
            <HolidaySection />
          </Suspense>
        </div>
      </section>

      {/* ========== TRENDING - SOCIAL PROOF ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-pink-400" />
                Ramai Orang Check
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Events paling popular minggu ni
              </p>
            </div>
          </div>
          <Suspense fallback={<SectionSkeleton />}>
            <TrendingSection onEventClick={(event) => navigate(`/event/${event.id}`)} />
          </Suspense>
        </div>
      </section>

      {/* ========== MORE QUICK LISTS ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ConcertsQuickOptimized />
            <BoringWeekendFixOptimized />
          </div>
        </div>
      </section>

      {/* ========== CATEGORIES - MOOD FILTER ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl font-bold text-foreground mb-6">
            Pilih Ikut Mood
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((category, index) => {
              const config = categoryConfig[category.slug];
              const Icon = config?.icon || Sparkles;
              
              return (
                <div key={category.id}>
                  <Link
                    to={`/events?category=${category.slug}`}
                    className={`block p-5 rounded-2xl category-${category.slug} border hover-lift cursor-pointer`}
                    data-testid={`category-${category.slug}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className={`h-4 w-4 text-${config?.color}-400`} />
                      <span className="text-xs text-muted-foreground font-medium">{category.count} Events</span>
                    </div>
                    <h3 className="font-bold text-foreground text-sm leading-tight">
                      {category.name}
                    </h3>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== COUNTDOWN - ANTICIPATION ========== */}
      {nextEvent && (
        <section className="px-6 md:px-12 lg:px-24 pb-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-6">
              Event Seterusnya
            </h2>
            <Suspense fallback={<SectionSkeleton />}>
              <CountdownHero event={nextEvent} onEventPassed={fetchNextEvent} />
            </Suspense>
          </div>
        </section>
      )}

      {/* ========== SUBMIT EVENT - SOFT CTA ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl p-6 md:p-8 border border-teal-500/20">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg font-bold text-foreground mb-2">
                  Ada event? Tambah sini.
                </h3>
                <p className="text-sm text-muted-foreground">
                  Percuma. Approval 24 jam.
                </p>
              </div>
              <Button
                onClick={() => setSubmitModalOpen(true)}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl px-6"
                data-testid="submit-event-cta"
              >
                <Plus className="h-4 w-4 mr-2" />
                Submit Event
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ========== BLOG - GUIDES ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-violet-400" />
              Guides
            </h2>
            <Link to="/blog" className="text-violet-400 hover:text-violet-300 text-sm flex items-center gap-1 font-medium">
              Semua artikel <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: "Top 50 Events KL 2026", slug: "top-events-kuala-lumpur-2026", category: "Guide" },
              { title: "100+ Event Percuma", slug: "free-events-malaysia-2026", category: "Free" },
              { title: "Jadual Konsert 2026", slug: "concert-schedule-malaysia-2026", category: "Music" }
            ].map((article, i) => (
              <div key={i}>
                <Link
                  to={`/blog/${article.slug}`}
                  className="block glass-card rounded-xl p-4 border hover:border-violet-500/50 transition-all group"
                >
                  <span className="text-xs text-violet-400 font-medium">{article.category}</span>
                  <h3 className="text-sm font-semibold text-foreground mt-1 group-hover:text-violet-400 transition-colors">
                    {article.title}
                  </h3>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHATSAPP SHARE CTA ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-4xl mx-auto">
          <WhatsAppShareCard 
            title="Ajak kawan plan event sama-sama! 🎉"
            subtitle="Share UPNEXT ke group WhatsApp korang - senang nak decide pergi mana"
          />
        </div>
      </section>

      {/* ========== BOOKMARK REMINDER - IDENTITY LOCK ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center p-6 rounded-2xl border border-dashed border-white/20">
            <p className="text-sm text-muted-foreground mb-2">
              <span className="text-foreground font-medium">Tip:</span> Bookmark page ni.
            </p>
            <p className="text-xs text-muted-foreground">
              Ramai check setiap Khamis untuk plan weekend. Tak perlu scroll social media lagi.
            </p>
          </div>
        </div>
      </section>

      {/* ========== NEWSLETTER - RETENTION ========== */}
      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="max-w-4xl mx-auto">
          <Suspense fallback={<SectionSkeleton />}>
            <NewsletterSection />
          </Suspense>
        </div>
      </section>

      <Footer />
      
      {/* Floating elements - deferred */}
      <Suspense fallback={null}>
        <BookmarkTrigger />
        <WhatsAppShareBanner />
      </Suspense>
      
      {/* WhatsApp Floating Button - always visible */}
      <WhatsAppFloatingButton />
      
      <Suspense fallback={null}>
        <SubmitEventModal
          open={submitModalOpen}
          onClose={() => setSubmitModalOpen(false)}
        />
      </Suspense>
    </div>
  );
}

export default function HomePage() {
  return (
    <HomeDataProvider>
      <HomeContent />
    </HomeDataProvider>
  );
}
